import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { theme } from '@vimily/bonjoro-ui-components';

import {
  TestimonialQuestion,
  TestimonialResponseType,
} from '~/types/models/testimonial';
import { questionnaireCardsColors } from '~/App/Components/QuestionnaireCard';
import {
  RichText,
  defaultEmptyRichTextValue,
  parseStringToRichText,
  parseRichTextToSlateData,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import {
  QuestionProps,
  QuestionItem,
} from '../Components/Questionnaire/Question';

type Question = TestimonialQuestion & QuestionProps;

export type QuestionsState = Question[];

const DEFAULT_QUESTIONS = [
  'Introduce yourself, and tell us what you love about our product / service.',
  'How exactly do you use our product / service, and how has this positively impacted your life or business?',
  'Why would you recommend our product / service to others?',
];

const emptyQuestion: Question = {
  uuid: '',
  bg_color: theme.colors.yellow[100],
  question: '',
  question_rich: parseRichTextToSlateData(defaultEmptyRichTextValue),
  value: defaultEmptyRichTextValue,
  placeholder: 'Type in your question here',
  response_types: [TestimonialResponseType.Text, TestimonialResponseType.Video],
  response_count: 0,
};

export const initialState: QuestionsState = [emptyQuestion];

const testimonialQuestionsSlice = createSlice({
  name: 'testimonial-questions',
  initialState,
  reducers: {
    addQuestion(state: QuestionsState) {
      state.push({
        ...emptyQuestion,
        value: parseStringToRichText(DEFAULT_QUESTIONS[state.length] ?? ''),
        color: questionnaireCardsColors[state.length],
        responseTypes: emptyQuestion.response_types,
        response_count: 0,
      });
      return state;
    },
    updateQuestion(state: QuestionsState, action: PayloadAction<QuestionItem>) {
      const payload = action.payload;

      return state.map((question, index) =>
        index === payload.index
          ? {
              ...question,
              value: payload.value as RichText,
              bg_color: payload.color as string,
              response_types:
                payload.responseTypes as TestimonialResponseType[],
            }
          : question
      );
    },
    deleteQuestion(state: QuestionsState, action: PayloadAction<number>) {
      const updatedState = state.filter((_, i) => i !== action.payload);

      return updatedState.map((question, index) => ({
        ...question,
        bg_color: question?.bg_color ?? questionnaireCardsColors[index],
        color: question?.bg_color ?? questionnaireCardsColors[index],
        response_types: question?.response_types,
        responseTypes: question?.response_types,
      }));
    },
  },
});

export const actions = testimonialQuestionsSlice.actions;
export default testimonialQuestionsSlice.reducer;
